import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import LargeCard from '@/components/LargeCard';
import {
  isKanatiCategory,
  isKanatiServiceTicket
} from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import {
  WARNING_MESSAGE_TICKET_WATCHERS_SERVICE,
  WARNING_MESSAGE_TICKET_WATCHERS_THREAT
} from '@/features/TicketWatchers/TicketWatchers.constants';
import useStaticData from '@/hooks/StaticData.hooks';
import { useFetchSettings } from '@/pages/Administration/Administration.store';
import {
  organizationsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import {
  AI_SOC_TEAM_QUEUE,
  hasOrganizationWatchers,
  setFollowupDate,
  TICKET_TYPE,
  ticketAtom,
  ticketIsEditingAtom,
  useFetchAssigneesList,
  useFetchOwnersList,
  useFetchWatchersList
} from '@/pages/Ticket';
import type { Watcher } from '@/pages/Ticket/Ticket.types';
import {
  isSilencedTicketOrgWatcherService,
  isSilencedTicketOrgWatcherThreat
} from '@/pages/Ticket/Ticket.utils';
import {
  TICKET_ORIGINATION_SOURCE,
  TICKET_STATUS
} from '@/pages/Tickets/Tickets.constants';

import TicketNextAction from '../TicketNextAction/TicketNextAction';
import { TicketWatchers } from '../TicketWatchers';
import { TEAM_QUEUE } from './TicketCreateGeneralInformation.constants';

const TicketCreateGeneralInformation = () => {
  const [{ id: selectedOrgId, label: selectedOrgLabel }] = useAtom(
    selectedOrganizationAtom
  );
  const [
    {
      assignee,
      customer_ticket_id,
      external_ticket_id,
      investigate_url,
      followup_date,
      next_action,
      organization,
      origination_source,
      owner,
      team_queue,
      ticket_category,
      ticket_status,
      ticket_type,
      ticket_watchers
    }
  ] = useAtom(ticketAtom);
  const [ticketIsEditing] = useAtom(ticketIsEditingAtom);
  const [organizations] = useAtom(organizationsAtom);
  const { data: settings } = useFetchSettings();
  const { isFetching: isFetchingOwners, data: owners } = useFetchOwnersList();
  const { isFetching: isFetchingAssignees, data: assignees } =
    useFetchAssigneesList(selectedOrgLabel);
  const { watchersPageOptions: watchersOptions } = useFetchWatchersList(
    'page',
    ticketIsEditing ? 'selectedTicketOrg' : 'selectedOrg'
  );
  const { categories } = useStaticData();

  //-----------------------------------------------------------------
  const { control, formState, getValues, setValue } = useFormContext();
  const [
    selectedAssigneeId,
    selectedOwnerId,
    teamQueue,
    ticketPriority,
    watchers
  ] = useWatch({
    control,
    name: [
      'assignee',
      'owner',
      'team_queue',
      'ticket_priority',
      'ticket_watchers'
    ]
  });

  const validateWatchers = (): boolean => {
    // if the ticket is being created or edited with a status of draft or needs review, bypass the validation
    if (
      !ticketIsEditing ||
      [TICKET_STATUS.DRAFT, TICKET_STATUS.NEEDS_REVIEW].includes(
        ticket_status?.name as TICKET_STATUS
      )
    )
      return true;

    const formWatchers: Watcher[] = getValues().ticket_watchers;
    const hasOrgWatchers = hasOrganizationWatchers(
      formWatchers.map((w) => w.userId),
      watchersOptions,
      organization?.id || 0
    );

    return hasOrgWatchers;
  };

  //-----------------------------------------------------------------
  // filter categories based on ticket type
  const isKanatiTicket = isKanatiServiceTicket(
    ticket_type,
    ticket_category?.id
  );
  const categoriesList = sortBy(categories, 'name').filter(
    (category) =>
      category.ticket_type === ticket_type &&
      (!isKanatiCategory(category.id) ||
        (ticketIsEditing &&
          isKanatiTicket &&
          category.id === ticket_category?.id))
  );

  const selectedAssignee = useMemo(() => {
    if (assignees.length === 0) return null;
    return assignees.find((o) => o.id === selectedAssigneeId) || null;
  }, [assignees, selectedAssigneeId]);

  const selectedOwner = useMemo(() => {
    if (owners.length === 0) return null;
    return owners.find((o) => o.id === selectedOwnerId) || null;
  }, [owners, selectedOwnerId]);

  const watcherAlertMessage = useMemo(() => {
    if (ticket_type === TICKET_TYPE.THREAT) {
      return WARNING_MESSAGE_TICKET_WATCHERS_THREAT;
    }
    return WARNING_MESSAGE_TICKET_WATCHERS_SERVICE;
  }, [ticketPriority]);

  const silentOrgWatchers = useMemo(() => {
    return watchers.filter((watcher: Watcher) => {
      return (
        !watcher.internal &&
        (isSilencedTicketOrgWatcherThreat(
          ticketPriority,
          ticket_type,
          watcher
        ) ||
          isSilencedTicketOrgWatcherService(ticket_type, watcher))
      );
    });
  }, [ticketPriority, watchers]);

  const watcherCount = useMemo(() => {
    const isDefaultOrgWatchersOptionSelected = watchers.some(
      (watcher: Watcher) => watcher.userId === 0
    );

    return isDefaultOrgWatchersOptionSelected
      ? (watchers?.length ?? 0) - 1
      : (watchers?.length ?? 0);
  }, [watchers]);

  // sets the default value of the organization select to the current org
  // a change of the organization drop-down in the topbar triggers a change of the organization select
  useEffect(() => {
    // only set the default value if the ticket is not being edited
    if (!ticketIsEditing && selectedOrgId > 0 && organizations.length > 0) {
      setValue('organization', selectedOrgId);
    }
  }, [organizations, selectedOrgId, setValue, ticketIsEditing]);

  // if user reloads the page while editing
  useEffect(() => {
    if (ticketIsEditing) {
      setValue('customer_ticket_id', customer_ticket_id);
      setValue('external_ticket_id', external_ticket_id);
      setValue('followup_date', setFollowupDate(followup_date));
      setValue('next_action', next_action);
      setValue('organization', organization?.id);
      setValue('owner', owner?.id);
      setValue('assignee', assignee?.id);
      setValue('team_queue', team_queue);
      setValue('ticket_category', ticket_category?.id);
      setValue('ticket_watchers', ticket_watchers);
      setValue('external_ticket_id', external_ticket_id);
      setValue('investigate_url', investigate_url);
    }
  }, [
    assignee,
    customer_ticket_id,
    external_ticket_id,
    investigate_url,
    followup_date,
    organization,
    owner,
    setValue,
    team_queue,
    ticket_category,
    ticket_watchers,
    ticketIsEditing
  ]);

  useEffect(() => {
    // set the default value of the owner select
    const socTeamUser = owners.find((item) => item.name === 'SOC Team');
    if (teamQueue === 'SOC') {
      setValue('owner', socTeamUser?.id);
    }
  }, [owners, setValue, teamQueue]);

  const handleTeamQueueChange = (
    event: SelectChangeEvent,
    onChange: (event: SelectChangeEvent) => void
  ) => {
    const isAiSocDisabled =
      event.target.value === AI_SOC_TEAM_QUEUE &&
      origination_source !== TICKET_ORIGINATION_SOURCE.AI_SOC;
    if (isAiSocDisabled) {
      return;
    }
    onChange(event);
  };

  return (
    <LargeCard title="General Information">
      <Stack spacing={2}>
        {((organizations.length > 0 && selectedOrgId !== 0) ||
          (organizations.length > 0 && ticketIsEditing)) && (
          <div>
            <InputLabel htmlFor="organization-select">
              Organization *
            </InputLabel>
            <FormControl fullWidth>
              <Controller
                control={control}
                name="organization"
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Select
                    id="organization-select"
                    defaultValue=""
                    disabled
                    error={!!formState.errors.organization}
                    {...field}
                  >
                    {organizations?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
        )}
        {categories.length > 0 && (
          <div>
            <InputLabel htmlFor="category-select">Category *</InputLabel>
            <FormControl fullWidth>
              <Controller
                control={control}
                name="ticket_category"
                rules={{
                  required: true,
                  validate: (value) =>
                    isKanatiTicket
                      ? value === ticket_category?.id
                      : !isKanatiCategory(value)
                }}
                render={({ field }) => (
                  <Select
                    id="category-select"
                    error={!!formState.errors.ticket_category}
                    {...field}
                    disabled={!!ticketIsEditing && isKanatiTicket}
                  >
                    {categoriesList.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
        )}
        <div>
          <InputLabel htmlFor="team-queue-select">Team Queue *</InputLabel>
          <FormControl fullWidth>
            <Controller
              control={control}
              name="team_queue"
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Select
                  id="team-queue-select"
                  error={!!formState.errors.team_queue}
                  {...field}
                  onChange={(event) =>
                    handleTeamQueueChange(event, field.onChange)
                  }
                >
                  {TEAM_QUEUE.map((item) => (
                    <MenuItem
                      key={item}
                      value={item}
                      disabled={
                        item === AI_SOC_TEAM_QUEUE &&
                        origination_source !== TICKET_ORIGINATION_SOURCE.AI_SOC
                      }
                    >
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </div>
        {!isFetchingOwners && (
          <div>
            <InputLabel htmlFor="owner-select">Owner</InputLabel>
            <FormControl fullWidth>
              <Controller
                control={control}
                name="owner"
                render={({ field }) => {
                  const { onChange } = field;
                  return (
                    <Autocomplete
                      getOptionLabel={(option) => option.name}
                      onChange={(_, newValue) => {
                        onChange(newValue ? newValue.id : null);
                      }}
                      options={owners}
                      renderInput={(params) => <TextField {...params} />}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{ typography: 'body1' }}
                        >
                          {option.name}
                        </Box>
                      )}
                      value={selectedOwner}
                    />
                  );
                }}
              />
            </FormControl>
          </div>
        )}
        {settings.enable_ticket_assignee && !isFetchingAssignees && (
          <div>
            <InputLabel htmlFor="assignee-select">Assignee</InputLabel>
            <FormControl fullWidth>
              <Controller
                control={control}
                name="assignee"
                render={({ field }) => {
                  const { onChange } = field;
                  return (
                    <Autocomplete
                      getOptionLabel={(option) => option.name}
                      onChange={(_, newValue) => {
                        onChange(newValue ? newValue.id : null);
                      }}
                      options={assignees}
                      renderInput={(params) => <TextField {...params} />}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{ typography: 'body1' }}
                        >
                          {option.name}
                        </Box>
                      )}
                      value={selectedAssignee}
                    />
                  );
                }}
              />
            </FormControl>
          </div>
        )}
        <div>
          <InputLabel htmlFor="followup-picker">Follow-Up *</InputLabel>
          <FormControl id="followup-picker" fullWidth>
            <Controller
              control={control}
              name="followup_date"
              rules={{
                required: true
              }}
              render={({ field }) => (
                <DatePicker
                  disablePast
                  slotProps={{
                    textField: {
                      error: !!formState.errors.followup
                    }
                  }}
                  {...field}
                />
              )}
            />
          </FormControl>
        </div>
        <div>
          <InputLabel htmlFor="jira-input">Jira ID</InputLabel>
          <FormControl fullWidth>
            <Controller
              control={control}
              name="external_ticket_id"
              render={({ field }) => (
                <TextField
                  id="jira-input"
                  error={!!formState.errors.external_ticket_id}
                  slotProps={{
                    htmlInput: {
                      maxLength: 20,
                      style: { textTransform: 'uppercase' }
                    }
                  }}
                  {...field}
                />
              )}
            />
          </FormControl>
        </div>
        <div>
          <InputLabel htmlFor="customer-ticket-id-input">
            Customer Ticket ID
          </InputLabel>
          <FormControl fullWidth>
            <Controller
              control={control}
              name="customer_ticket_id"
              render={({ field }) => (
                <TextField
                  id="customer-ticket-id-input"
                  error={!!formState.errors.customer_ticket_id}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  {...field}
                />
              )}
            />
          </FormControl>
        </div>
        {ticket_type === TICKET_TYPE.THREAT && (
          <div>
            <InputLabel htmlFor="investigate-url">Investigate URL</InputLabel>
            <FormControl id="investigate-url" fullWidth>
              <Controller
                control={control}
                name="investigate_url"
                rules={{
                  validate: (value) => {
                    try {
                      if (value) {
                        new URL(value);
                        return true;
                      }
                    } catch (_err) {
                      return false;
                    }
                    return undefined;
                  }
                }}
                render={({ field }) => (
                  <TextField
                    id="investigate-url"
                    error={!!formState.errors.investigate_url}
                    {...(!!formState.errors.investigate_url && {
                      helperText: 'Invalid URL'
                    })}
                    {...field}
                  />
                )}
              />
            </FormControl>
          </div>
        )}
        {ticketIsEditing && (
          <div>
            <InputLabel htmlFor="next-action">Next Action *</InputLabel>
            <FormControl id="next-action" fullWidth>
              <Controller
                control={control}
                name="next_action"
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <TicketNextAction
                    hasError={!!formState.errors.next_action}
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
            </FormControl>
          </div>
        )}
        <Divider />
        {ticketPriority && silentOrgWatchers.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>Watchers</AlertTitle>
            {watcherAlertMessage}
            <ul>
              {silentOrgWatchers.map((watcher: Watcher) => (
                <li key={watcher.userId}>{watcher.label}</li>
              ))}
            </ul>
          </Alert>
        )}
        <div>
          <Typography variant="h6" component="h2" gutterBottom>
            Watchers ({watcherCount})
          </Typography>
          <Controller
            control={control}
            name="ticket_watchers"
            rules={{
              validate: validateWatchers
            }}
            render={({ field }) => (
              <TicketWatchers
                buttonIcon={!ticketIsEditing}
                buttonLabel={ticketIsEditing ? 'Update' : 'Add'}
                disableOnCreateWithAllOrganizations={!ticketIsEditing}
                isTicketCreatePage
                onChange={field.onChange}
                ticketOrganizationId={
                  ticketIsEditing ? organization?.id : selectedOrgId
                }
                ticketPriority={ticketPriority}
                ticketType={ticketIsEditing ? ticket_type : TICKET_TYPE.THREAT}
                watchers={field.value}
                watchersOptions={watchersOptions}
              />
            )}
          />
        </div>
      </Stack>
    </LargeCard>
  );
};

export default TicketCreateGeneralInformation;
